import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import {PageTitle} from "../../../../Components/Landmarks";
import {SearchResultsContainer} from "../../../../Components/SearchResults";
import {Button, inputStyles, SelectField, TextField} from "../../../../Components/Inputs";
import {useInput} from "../../../../Components/Inputs/inputUtility";
import {LinkButton, ExternalLink} from "../../../../Components/Link";
import {locations} from "../../../../Utilities/Location";
import {allow} from "../../../../Components/Authorize";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {isObjectNullOrEmpty} from "../../../../Utilities/Types/objectUtilities";
import {loanLibraryCatalogApi} from "./loanLibraryCatalogApi";
import {LoanLibrarySearchResultsView} from "./LoanLibrarySearchResultsView";
import {emptyGuid} from "../../../../Utilities/commonUtilities";
import * as PolicyEvents from "../../../../Components/Authorize/policyEventsConstants";
import {loanLibraryQueueApi} from "../LoanLibraryQueue/loanLibraryQueueApi";
import {SYSTEM_TYPES} from "../../libraryConstants";
import {LOAN_LIBRARY_MEDIA_FORMAT_LIST} from "../loanLibraryConstants";
import { isRmtcAdmin } from "@/Utilities/LocalStorage/storageUtilities";


export const LoanLibraryCatalogContainer = ({
                                                handleApiCall,
                                                handleSetLoanLibraryCatalogSearchCriteria,
                                                loanLibraryCatalogSearchCriteria,
                                                redirect,
                                            }: ContextProps) => {

    const [catalogResources, setCatalogResources] = useState<Record<string, unknown>[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const keywordSearch = useInput("");
    const catalogNumberSearch = useInput("");
    const mediaFormatSearch = useInput("");

    const canManageCatalog = allow(PolicyEvents.MANAGE_LOAN_LIBRARY_CATALOG);

    const loadCatalogResourcesResult = (result: { loanLibraryCatalogItems?: Record<string, unknown>[]; totalRecords?: number }) => {
        setCatalogResources(result.loanLibraryCatalogItems || []);
        setTotalRecords(result.totalRecords || 0);
    };

    const getCatalogResources = () => {
        if (isObjectNullOrEmpty(loanLibraryCatalogSearchCriteria)) return;

        const updatedCriteria = {
            ...loanLibraryCatalogSearchCriteria,
            searchTerm: keywordSearch.value,
            catalogNumber: catalogNumberSearch.value,
            mediaFormat: mediaFormatSearch.value || null,
            loadSearchResults: true,
        };
        handleApiCall(
            () => loanLibraryCatalogApi.getLoanLibraryCatalogResources(updatedCriteria),
            loadCatalogResourcesResult
        );
    };

    const handleEdit = (catalogItemId: string) => {
        redirect(locations.LOAN_LIBRARY_CATALOG_ITEM.getLink([catalogItemId, SYSTEM_TYPES.LoanLibrary]).to);
    };

    const handleAddToQueue = (catalogItemId: string) => {
        handleApiCall(
            () => loanLibraryQueueApi.addToQueue(catalogItemId),
            () => {
                getCatalogResources();
            }
        );
    };

    const handleSearch = () => {
        if (isObjectNullOrEmpty(loanLibraryCatalogSearchCriteria)) return;
        getCatalogResources();
    };

    const noResults = isArrayNullOrEmpty(catalogResources);

    useEffect(() => {
        if (loanLibraryCatalogSearchCriteria?.loadSearchResults) {
            getCatalogResources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanLibraryCatalogSearchCriteria, keywordSearch.value]);

    useEffect(() => {
        if (!isObjectNullOrEmpty(loanLibraryCatalogSearchCriteria)) {
            getCatalogResources();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keywordSearch.value]);

    if (!loanLibraryCatalogSearchCriteria) return null;

    return (
        <>
            <div className="loanLibraryCatalog">
                <PageTitle h1="Loan Library Catalog" className="loanLibraryCatalog__title"/>

                <div className="loanLibraryCatalog__content">
                    <ul>
                        <li><strong>How It Works:</strong>
                            <ol>
                                <li><strong>Receive:</strong> Your order will be shipped to you via FedEx at no charge.</li>
                                <li><strong>Return:</strong> When you are finished with the materials, simply place them back in the original box and affix the included pre-paid FedEx return label to the outside.</li>
                                <li><strong>Ship:</strong> You have two easy options for returning your package:
                                    <ul>
                                        <li><strong>At School:</strong> Place the box at your school's designated FedEx pickup location—the driver will collect it automatically.</li>
                                        <li><strong>On the Go:</strong> Drop the package off at any authorized FedEx drop-off location, including FedEx Office, FedEx Ship Centers, and participating retailers like Walgreens or Dollar General.</li>
                                    </ul>
                                </li>
                            </ol>
                        </li>
                        <li>Want more assessment resources? Visit the <ExternalLink
                            label={`Assessment Resources page`}
                            url={`https://www.rmtcdhh.org/resources/assessment-tools`}/> ! If you have any questions, please contact <ExternalLink
                            label={`RMTC-DHH`}
                            url={`https://www.rmtcdhh.org/about-us/contact-us`}/> .</li>
                    </ul>
                </div>

                <div className="searchBox searchBox--loanLibrary">
                    <div className="searchBox__title">Search the Catalog</div>
                    <div className="searchBox__input">
                        <TextField
                            label="Keyword Search box"
                            labelClass="show-for-sr"
                            name="txtSearch"
                            placeholder="Product Name, Author, or Keywords"
                            {...keywordSearch}
                        />

                        <TextField
                            label="Catalog Number Search box"
                            labelClass="show-for-sr"
                            name="catalogNumberSearch"
                            placeholder="Catalog Number"
                            {...catalogNumberSearch}
                        />

                        <SelectField
                            label="Media Format"
                            name="mediaFormatSearch"
                            options={LOAN_LIBRARY_MEDIA_FORMAT_LIST}
                            includeDefaultOption
                            defaultOption="All Media Formats"
                            hideLabel
                            {...mediaFormatSearch}
                        />

                        <div className="searchBox__buttonWrapper">
                            <Button
                                buttonType={inputStyles.button.types.SEARCH}
                                label="Search"
                                name="btnSearch"
                                onClick={handleSearch}
                                isPrimary
                            />
                        </div>
                    </div>
                </div>

                <div className="loanLibraryCatalog__actions">
                    <LinkButton {...locations.LOAN_LIBRARY_WHATS_NEW.getLink()} />
                    <LinkButton {...locations.LOAN_LIBRARY_QUEUE.getLink()} />
                    <LinkButton {...locations.LOAN_LIBRARY_HISTORY.getLink()} />
                    {canManageCatalog && (
                        <LinkButton
                            {...locations.LOAN_LIBRARY_CATALOG_ITEM.getLink([emptyGuid, SYSTEM_TYPES.LoanLibrary])}
                            label="Add New Item"
                        />
                    )}
                    {canManageCatalog && (
                        <>
                            <LinkButton {...locations.LIBRARY_VENDOR.getLink([emptyGuid, SYSTEM_TYPES.LoanLibrary])} />
                            <LinkButton {...locations.LOAN_LIBRARY_MANUFACTURER.getLink([emptyGuid])} />
                        </>
                    )}
                    {
                        isRmtcAdmin() && (
                            <LinkButton {...locations.SHIPPING_LOCATION_ADD.link} label={"Manage Shipping Locations"} />                                 
                        )
                    }                    
                </div>
            </div>

            <SearchResultsContainer>
                {catalogResources && !noResults && (
                    <LoanLibrarySearchResultsView
                        catalogResources={catalogResources}
                        catalogSearchCriteria={loanLibraryCatalogSearchCriteria}
                        canManageCatalog={canManageCatalog}
                        handleAddToQueue={handleAddToQueue}
                        handleEdit={handleEdit}
                        handleSetSearchCriteria={handleSetLoanLibraryCatalogSearchCriteria}
                        totalRecords={totalRecords}
                    />
                )}
                {noResults && (
                    <p className="text-center">
                        No results found. Please try a different search or contact
                        RMTC at information@rmtc.org if you believe this is in
                        error.
                    </p>
                )}
            </SearchResultsContainer>
        </>
    );
};

